/**
 * Library Tab - Browse All Research & Resources
 * 
 * Two sub-sections:
 * - Research: 719 peer-reviewed sources sorted by citation count
 * - Resources: Interpreters + Frameworks sorted by citation count
 * 
 * Author: Coach Cedar Development Team
 * Date: December 11, 2025
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useUserCitedSources,
  useResources,
  useMyFrameworks,
  UserCitedSource,
  Resource,
  Framework
} from '../hooks/useResearchLibrary';
import { FileText, BookOpen, ExternalLink, MessageCircle, ChevronRight, Star, Target } from 'lucide-react';
import { ContextualChatButton } from './ContextualChatButton';
import './InsightsV2.css';
import '../styles/card-system.css';

type LibraryFilter = 'research' | 'resources';

interface LibraryTabProps {
  userId: string;
}

export const LibraryTab: React.FC<LibraryTabProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<LibraryFilter>('research');
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);

  const librarySliderTrackRef = useRef<HTMLDivElement>(null);
  const libraryTouchStartXRef = useRef<number | null>(null);

  // Fetch data - ONLY sources that have been used for this user
  const { data: sourcesData, loading: sourcesLoading, error: sourcesError } = useUserCitedSources(limit, offset);
  const { data: resourcesData, loading: resourcesLoading } = useResources();
  const { data: frameworksData, loading: frameworksLoading } = useMyFrameworks();

  const sources = sourcesData?.sources || [];
  const totalSourcesCount = sourcesData?.total_count || 0;
  const resources = resourcesData?.resources || [];
  const frameworks = frameworksData?.frameworks || [];

  const loading = filter === 'research' ? sourcesLoading : (resourcesLoading || frameworksLoading);

  const handleChatWithCedar = (type: string, item: any) => {
    navigate('/chat', {
      state: {
        from: 'research-library',
        context: {
          type,
          id: item.id,
          name: item.name || item.title || item.framework_name,
          initialMessage: `Tell me more about ${item.name || item.title || item.framework_name}.`
        }
      }
    });
  };

  const handleResourceClick = (resourceId: string) => {
    navigate(`/insights/resource/${resourceId}`);
  };

  const handleViewFullStudy = (doi: string) => {
    window.open(`https://doi.org/${doi}`, '_blank');
  };

  const handleLoadMore = () => {
    setOffset(offset + limit);
  };

  if (loading && sources.length === 0 && resources.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sub-Filter Slider - Research | Resources with sliding pill and swipe */}
      <div className="px-4">
        <div
          ref={librarySliderTrackRef}
          className="library-sub-slider-track"
          role="tablist"
          aria-label="Library filters"
          onTouchStart={(e) => {
            libraryTouchStartXRef.current = e.targetTouches[0].clientX;
          }}
          onTouchEnd={(e) => {
            const startX = libraryTouchStartXRef.current;
            if (startX == null) return;
            const endX = e.changedTouches[0].clientX;
            const deltaX = endX - startX;
            const threshold = 40;
            if (deltaX < -threshold && filter === 'research') setFilter('resources');
            else if (deltaX > threshold && filter === 'resources') setFilter('research');
            libraryTouchStartXRef.current = null;
          }}
          onMouseDown={(e) => {
            libraryTouchStartXRef.current = e.clientX;
          }}
          onMouseUp={(e) => {
            const startX = libraryTouchStartXRef.current;
            if (startX == null) return;
            const deltaX = e.clientX - startX;
            const threshold = 40;
            if (deltaX < -threshold && filter === 'research') setFilter('resources');
            else if (deltaX > threshold && filter === 'resources') setFilter('research');
            libraryTouchStartXRef.current = null;
          }}
          onMouseLeave={() => {
            libraryTouchStartXRef.current = null;
          }}
        >
          <div
            className="library-sub-slider-pill"
            style={{
              transform: filter === 'research' ? 'translateX(0)' : 'translateX(100%)',
            }}
            aria-hidden
          />
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'research'}
            className={`library-sub-slider-segment ${filter === 'research' ? 'library-sub-slider-active' : ''}`}
            onClick={() => setFilter('research')}
          >
            <FileText size={14} />
            Research
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'resources'}
            className={`library-sub-slider-segment ${filter === 'resources' ? 'library-sub-slider-active' : ''}`}
            onClick={() => setFilter('resources')}
          >
            <BookOpen size={14} />
            Resources
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="px-4">
        <div className="insights-notice">
          {filter === 'research' ? (
            <>
              <FileText size={16} style={{ color: '#1f6c4f', marginTop: '2px' }} />
              <div>
                <strong>{totalSourcesCount} peer-reviewed {totalSourcesCount === 1 ? 'study' : 'studies'}</strong> cited for your coaching
              </div>
            </>
          ) : (
            <>
              <BookOpen size={16} style={{ color: '#1f6c4f', marginTop: '2px' }} />
              <div>
                <strong>{frameworks.length} frameworks</strong> for practical guidance
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 space-y-3">
        {filter === 'research' ? (
          // RESEARCH TAB - Sources cited for this user
          <>
            {sourcesError ? (
              <div className="insights-card error-card">
                <div className="insights-card-title">
                  <FileText size={18} />
                  Error
                </div>
                <div className="insights-card-body">Error loading sources: {sourcesError}</div>
              </div>
            ) : sources.length === 0 ? (
              <div className="insights-card">
                <div className="insights-card-title">
                  <FileText size={18} style={{ color: '#1f6c4f' }} />
                  No Sources Yet
                </div>
                <div className="insights-card-body">
                  As Cedar coaches you, research sources will appear here. Chat with Cedar about your goals to see the evidence behind your recommendations.
                </div>
              </div>
            ) : (
              <>
                {sources.map((source) => (
                  <ResearchSourceCard
                    key={source.id}
                    source={source}
                    onChatClick={() => handleChatWithCedar('source', source)}
                    onViewStudy={() => handleViewFullStudy(source.doi)}
                  />
                ))}

                {/* Load More Button */}
                {sources.length < totalSourcesCount && (
                  <button
                    onClick={handleLoadMore}
                    className="insights-btn"
                    style={{ width: '100%' }}
                    disabled={sourcesLoading}
                  >
                    {sourcesLoading ? 'Loading...' : `Load More (${totalSourcesCount - sources.length} remaining)`}
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          // RESOURCES TAB - Frameworks FIRST, Interpreters accessible via framework cards
          <>
            {/* Frameworks Section - PRIMARY VIEW */}
            {frameworks.length > 0 && (
              <>
                <h3 className="text-xs font-semibold uppercase tracking-wide mt-2 mb-2" style={{ color: 'rgba(17, 60, 43, 0.75)' }}>
                  Frameworks ({frameworks.length})
                </h3>
                {frameworks.map((framework) => (
                  <FrameworkCard
                    key={framework.id}
                    framework={framework}
                    onChatClick={() => handleChatWithCedar('framework', framework)}
                    onCreatorClick={(resourceId) => handleResourceClick(resourceId)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// RESEARCH SOURCE CARD
// ============================================

interface ResearchSourceCardProps {
  source: UserCitedSource;
  onChatClick: () => void;
  onViewStudy: () => void;
}

const ResearchSourceCard: React.FC<ResearchSourceCardProps> = ({ source, onChatClick, onViewStudy }) => {
  const [expanded, setExpanded] = useState(false);

  const getEvidenceStars = (strength: string) => {
    const stars: Record<string, string> = {
      strong: '⭐⭐⭐',
      moderate: '⭐⭐',
      emerging: '⭐',
      preliminary: '⭐'
    };
    return stars[strength] || '⭐';
  };

  const getEvidenceColor = (strength: string) => {
    const colors: Record<string, string> = {
      strong: '#15803d',
      moderate: '#f59e0b',
      emerging: '#3b82f6',
      preliminary: '#6b7280'
    };
    return colors[strength] || '#6b7280';
  };

  return (
    <div className="mypath-card" style={{ minHeight: '120px', position: 'relative' }}>
      {/* White content overlay - NO header, just content */}
      <div className="bg-white rounded-lg p-5">
        
        {/* Title - Bold and larger */}
        <h3 className="font-bold text-base mb-2" style={{ color: '#113C2B', lineHeight: '1.4' }}>
          {source.title}
        </h3>

        {/* Authors and Year - One line */}
        <p className="text-sm mb-1" style={{ color: 'rgba(17, 60, 43, 0.7)' }}>
          {source.authors.slice(0, 3).join(', ')}
          {source.authors.length > 3 && ` et al.`}
          {' • '}
          {source.publication_year}
        </p>

        {/* Journal - Italicized */}
        {source.journal && (
          <p className="text-xs italic mb-3" style={{ color: 'rgba(17, 60, 43, 0.6)' }}>
            {source.journal}
          </p>
        )}

        {/* Evidence and Consensus - Enhanced */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium" style={{ color: 'rgba(17, 60, 43, 0.7)' }}>Evidence:</span>
            <span className="text-sm">{getEvidenceStars(source.evidence_strength)}</span>
            <span 
              className="text-xs font-semibold px-2 py-0.5 rounded"
              style={{ 
                backgroundColor: getEvidenceColor(source.evidence_strength) + '20',
                color: getEvidenceColor(source.evidence_strength)
              }}
            >
              {source.evidence_strength}
            </span>
          </div>
          {source.consensus_level !== null && (
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium" style={{ color: 'rgba(17, 60, 43, 0.7)' }}>Consensus:</span>
              <span className="text-xs font-semibold" style={{ color: '#15803d' }}>
                {Math.round((source.consensus_level || 0) * 100)}%
              </span>
            </div>
          )}
        </div>

        {/* Key Findings - Expandable */}
        {expanded && source.key_findings && source.key_findings.length > 0 && (
          <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: 'var(--cedar-subtle)' }}>
            <h4 className="text-xs font-semibold uppercase mb-2" style={{ color: 'rgba(17, 60, 43, 0.75)', letterSpacing: '0.05em' }}>
              Key Findings
            </h4>
            <ul className="space-y-2">
              {source.key_findings.map((finding, idx) => (
                <li 
                  key={idx} 
                  className="text-sm pl-4 relative before:content-['•'] before:absolute before:left-0 before:font-bold" 
                  style={{ color: 'rgba(17, 60, 43, 0.85)', lineHeight: '1.5' }}
                >
                  {finding}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Context Tags - Sport/Goal contexts */}
        {expanded && (source.sport_contexts || source.goal_contexts) && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold uppercase mb-2" style={{ color: 'rgba(17, 60, 43, 0.75)', letterSpacing: '0.05em' }}>
              Context Tags
            </h4>
            <div className="flex flex-wrap gap-2">
              {source.sport_contexts?.map((ctx, idx) => (
                <span 
                  key={`sport-${idx}`} 
                  className="px-2 py-1 rounded text-xs font-medium"
                  style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}
                >
                  {ctx.replace(/_/g, ' ')}
                </span>
              ))}
              {source.goal_contexts?.map((ctx, idx) => (
                <span 
                  key={`goal-${idx}`} 
                  className="px-2 py-1 rounded text-xs font-medium"
                  style={{ backgroundColor: '#dcfce7', color: '#15803d' }}
                >
                  {ctx.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {/* 🐛 FIX (Feb 3, 2026): Pass context prop, not onClick */}
          <ContextualChatButton
            context={{
              type: 'research',
              id: source.id,
              title: source.title,
              description: `Research by ${source.authors.slice(0, 2).join(', ')} (${source.publication_year})`,
              metadata: {
                authors: source.authors,
                journal: source.journal,
                doi: source.doi,
                evidence_strength: source.evidence_strength,
                consensus_level: source.consensus_level
              }
            }}
            size="medium"
            variant="clean"
          />

          {/* Study button - primary Cedar green */}
          <button
            onClick={onViewStudy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: 'var(--cedar-primary)',
              color: 'white'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-dark)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-primary)'}
          >
            <ExternalLink size={14} />
            Study
          </button>

          {/* Show Details button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: 'var(--cedar-subtle)',
              color: 'var(--cedar-primary)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-limestone)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)'}
          >
            {expanded ? 'Show Less' : 'Show Details'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// INTERPRETER CARD
// ============================================

interface InterpreterCardProps {
  resource: Resource;
  onCardClick: () => void;
  onChatClick: () => void;
}

const InterpreterCard: React.FC<InterpreterCardProps> = ({ resource, onCardClick, onChatClick }) => {
  const getPeerRecognitionStars = (score: number) => {
    const stars = Math.round(score * 5);
    return '⭐'.repeat(stars);
  };

  return (
    <div className="insights-card" style={{ cursor: 'pointer' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1" onClick={onCardClick}>
          <h3 className="font-semibold" style={{ color: '#113C2B', fontFamily: '"Inter", system-ui, -apple-system, sans-serif' }}>{resource.name}</h3>
          {resource.credentials && resource.credentials.length > 0 && (
            <p className="text-sm" style={{ color: 'rgba(17, 60, 43, 0.65)' }}>{resource.credentials.join(', ')}</p>
          )}
        </div>
        <div className="text-xs" style={{ color: 'rgba(17, 60, 43, 0.55)' }}>
          {getPeerRecognitionStars(resource.peer_recognition_score || 0)}
        </div>
      </div>

      {resource.primary_expertise && resource.primary_expertise.length > 0 && (
        <div className="mb-3">
          <p className="text-sm" style={{ color: 'rgba(17, 60, 43, 0.8)' }}>
            <span className="font-medium">Expertise:</span>{' '}
            {resource.primary_expertise.slice(0, 3).join(', ')}
          </p>
        </div>
      )}

      {resource.platform_types && resource.platform_types.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {resource.platform_types.map((platform, idx) => (
              <span
                key={idx}
                className="insights-badge"
              >
                {platform}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="insights-btn-row">
        <button
          onClick={onCardClick}
          className="insights-btn fill"
          style={{ flex: 1 }}
        >
          View Details
          <ChevronRight size={14} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onChatClick();
          }}
          className="insights-btn"
        >
          <MessageCircle size={14} />
          Chat
        </button>
      </div>
    </div>
  );
};

// ============================================
// FRAMEWORK CARD
// ============================================

interface FrameworkCardProps {
  framework: Framework;
  onChatClick: () => void;
  onCreatorClick: (resourceId: string) => void;
}

const FrameworkCard: React.FC<FrameworkCardProps> = ({ framework, onChatClick, onCreatorClick }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mypath-card" style={{ minHeight: '120px', position: 'relative' }}>
      {/* White content overlay - NO header, just content */}
      <div className="bg-white rounded-lg p-5">
        
        {/* Framework Title with Icon - Bold and larger */}
        <div className="flex items-center gap-2 mb-2">
          <Target size={20} className="flex-shrink-0" style={{ color: 'var(--cedar-primary)' }} />
          <h3 className="font-bold text-base" style={{ color: '#113C2B', lineHeight: '1.4' }}>
            {framework.framework_name}
          </h3>
        </div>

        {/* Evidence Basis Badge */}
        <div className="mb-3">
          <span 
            className="inline-block px-3 py-1 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: '#dcfce7',
              color: '#15803d'
            }}
          >
            {framework.evidence_basis.replace('_', ' ')}
          </span>
        </div>

        {/* From / Source Section - Full width, left-justified
            Coverage Sprint Phase F (Apr 2026): institutional frameworks
            (ACSM/AHA/ACOG/ADA committee statements) have resource_id NULL.
            For those rows, show "Source: <source_work>" instead of the
            interpreter-link "From: <name>" pattern. Native (InsightsScreen.native.tsx)
            already null-checks resource_name; this brings web parity. */}
        {framework.resource_id ? (
          <div className="mb-3">
            <span className="text-sm" style={{ color: 'rgba(17, 60, 43, 0.65)' }}>From: </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCreatorClick(framework.resource_id);
              }}
              className="text-sm font-medium hover:underline"
              style={{
                color: 'var(--cedar-primary)',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer'
              }}
            >
              {framework.resource_name}
            </button>
            {framework.resource_credentials && framework.resource_credentials.length > 0 && (
              <span className="text-xs ml-1" style={{ color: 'rgba(17, 60, 43, 0.55)' }}>
                ({framework.resource_credentials.join(', ')})
              </span>
            )}
          </div>
        ) : framework.source_work ? (
          <div className="mb-3">
            <span className="text-sm" style={{ color: 'rgba(17, 60, 43, 0.65)' }}>Source: </span>
            <span
              className="text-sm font-medium"
              style={{ color: 'var(--cedar-primary)' }}
            >
              {framework.source_work}
            </span>
          </div>
        ) : null}

        {/* Description */}
        {framework.description && (
          <p className="text-sm mb-4" style={{ color: 'rgba(17, 60, 43, 0.8)', lineHeight: '1.5' }}>
            {framework.description}
          </p>
        )}

        {/* Expanded Content */}
        {expanded && (
          <div className="space-y-3 mb-4">
            {framework.key_principles && framework.key_principles.length > 0 && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--cedar-subtle)' }}>
                <h4 className="text-xs font-semibold uppercase mb-2" style={{ color: 'rgba(17, 60, 43, 0.75)', letterSpacing: '0.05em' }}>
                  Key Principles
                </h4>
                <ul className="space-y-2">
                  {framework.key_principles.map((principle, idx) => (
                    <li 
                      key={idx} 
                      className="text-sm pl-4 relative before:content-['•'] before:absolute before:left-0 before:font-bold" 
                      style={{ color: 'rgba(17, 60, 43, 0.85)', lineHeight: '1.5' }}
                    >
                      {principle}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {framework.practical_guidelines && framework.practical_guidelines.length > 0 && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--cedar-subtle)' }}>
                <h4 className="text-xs font-semibold uppercase mb-2" style={{ color: 'rgba(17, 60, 43, 0.75)', letterSpacing: '0.05em' }}>
                  Practical Guidelines
                </h4>
                <ul className="space-y-2">
                  {framework.practical_guidelines.slice(0, 5).map((guideline, idx) => (
                    <li 
                      key={idx} 
                      className="text-sm pl-4 relative before:content-['•'] before:absolute before:left-0 before:font-bold" 
                      style={{ color: 'rgba(17, 60, 43, 0.85)', lineHeight: '1.5' }}
                    >
                      {guideline}
                    </li>
                  ))}
                </ul>
                {framework.practical_guidelines.length > 5 && (
                  <p className="text-xs mt-2 italic" style={{ color: 'rgba(17, 60, 43, 0.6)' }}>
                    + {framework.practical_guidelines.length - 5} more guidelines
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {/* 🐛 FIX (Feb 3, 2026): Pass context prop, not onClick */}
          <ContextualChatButton
            context={{
              type: 'research',
              id: framework.id,
              title: framework.framework_name,
              // Coverage Sprint Phase F: null-safe attribution. Institutional
              // frameworks (resource_id NULL) cite source_work; interpreter-
              // attached frameworks cite resource_name.
              description: framework.resource_name
                ? `Framework by ${framework.resource_name}`
                : framework.source_work
                  ? `Framework from ${framework.source_work}`
                  : 'Research framework',
              metadata: {
                resource_id: framework.resource_id,
                resource_name: framework.resource_name,
                evidence_basis: framework.evidence_basis,
                consensus_level: framework.consensus_level
              }
            }}
            size="medium"
            variant="clean"
          />

          {/* Show Details button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: 'var(--cedar-subtle)',
              color: 'var(--cedar-primary)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-limestone)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)'}
          >
            {expanded ? 'Show Less' : 'Show Details'}
          </button>
        </div>
      </div>
    </div>
  );
};


